import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatLastModified } from "../utils/dateFormatting";
import { AlertTriangle, Clock, CheckCircle, Send, FileWarning } from "lucide-react";
import "./DiscrepancyHoverCard.css";

const getStatusIcon = (status) => {
  switch (status) {
    case "Draft":
      return <FileWarning size={14} />;
    case "Waiting to Review":
      return <AlertTriangle size={14} />;
    case "Reviewed - not sent":
      return <Clock size={14} />;
    case "Sent":
      return <Send size={14} />;
    case "Resolved":
      return <CheckCircle size={14} />;
    default:
      return <FileWarning size={14} />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "Draft":
      return "#6b7280"; // gray
    case "Waiting to Review":
      return "#ef4444"; // red
    case "Reviewed - not sent":
      return "#f59e0b"; // amber
    case "Sent":
      return "#3b82f6"; // blue
    case "Resolved":
      return "#10b981"; // green
    default:
      return "#6b7280";
  }
};

const DiscrepancyHoverCard = ({ discrepancies, isVisible, onDiscrepancyClick, anchorElement }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);

  useEffect(() => {
    if (!isVisible || !anchorElement) return;

    const updatePosition = () => {
      const anchorRect = anchorElement.getBoundingClientRect();
      const cardWidth = 300; // min-width from CSS
      const cardHeight = cardRef.current?.offsetHeight || 200;

      // Calculate initial position (below and to the right of anchor)
      let top = anchorRect.bottom + 4; // 4px gap
      let left = anchorRect.right - cardWidth;

      // Adjust if card would overflow viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Horizontal adjustment
      if (left + cardWidth > viewportWidth - 10) {
        left = viewportWidth - cardWidth - 10;
      }
      if (left < 10) {
        left = 10;
      }

      // Vertical adjustment - show above if not enough space below
      if (top + cardHeight > viewportHeight - 10) {
        top = anchorRect.top - cardHeight - 4;
        // If still not enough space, position at bottom of viewport
        if (top < 10) {
          top = viewportHeight - cardHeight - 10;
        }
      }

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible, anchorElement]);

  if (!isVisible || !discrepancies || discrepancies.length === 0) {
    return null;
  }

  const handleClick = (e, discrepancyId) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDiscrepancyClick) {
      onDiscrepancyClick(e, discrepancyId);
    }
  };

  const cardContent = (
    <div
      ref={cardRef}
      className="discrepancy-hover-card"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className="discrepancy-hover-header">
        <h4>Discrepancies ({discrepancies.length})</h4>
      </div>
      <div className="discrepancy-hover-list">
        {discrepancies.map((discrepancy) => (
          <div
            key={discrepancy.id}
            className="discrepancy-hover-item"
            onClick={(e) => handleClick(e, discrepancy.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleClick(e, discrepancy.id);
              }
            }}
          >
            <div className="discrepancy-hover-main">
              <div className="discrepancy-hover-type">
                {discrepancy.discrepancyType}
              </div>
              <div
                className="discrepancy-hover-status"
                style={{ color: getStatusColor(discrepancy.discrepancyStatus) }}
              >
                {getStatusIcon(discrepancy.discrepancyStatus)}
                <span>{discrepancy.discrepancyStatus}</span>
              </div>
            </div>
            <div className="discrepancy-hover-meta">
              <div className="discrepancy-hover-date">
                {formatLastModified(discrepancy.createdAt)}
              </div>
              {discrepancy.createdByName && (
                <div className="discrepancy-hover-creator">
                  by {discrepancy.createdByName}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return createPortal(cardContent, document.body);
};

export default DiscrepancyHoverCard;